import React from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  IconButton,
  Chip,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Edit,
  Delete,
  ExpandMore,
  Add,
} from '@mui/icons-material';
import { PhaseDefinition } from '../../types';

interface PhaseListProps {
  phases: PhaseDefinition[];
  onEdit: (phase: PhaseDefinition) => void;
  onDelete: (phaseId: number) => void;
  onAdd: () => void;
}

const PhaseList: React.FC<PhaseListProps> = ({
  phases,
  onEdit,
  onDelete,
  onAdd,
}) => {
  const handleDeleteClick = (phase: PhaseDefinition) => {
    if (window.confirm(`Are you sure you want to delete phase "${phase.name}"?`)) {
      onDelete(phase.phaseId);
    }
  };

  if (phases.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          No phases defined yet. Add your first phase to get started.
        </Alert>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={onAdd}
        >
          Add First Phase
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Phases ({phases.length})
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={onAdd}
        >
          Add Phase
        </Button>
      </Box>

      <List>
        {phases.map((phase, index) => (
          <Accordion key={phase.phaseId} defaultExpanded={index === 0}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, mr: 2 }}>
                  {index + 1}. {phase.name}
                </Typography>
                {phase.exitActions.length > 0 && (
                  <Chip
                    label={`${phase.exitActions.length} exit action${phase.exitActions.length !== 1 ? 's' : ''}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                )}
                <Box sx={{ flexGrow: 1 }} />
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(phase);
                  }}
                  title="Edit"
                  color="primary"
                >
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(phase);
                  }}
                  title="Delete"
                  color="error"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 2 }}>
                {phase.description && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Description:</strong> {phase.description}
                  </Typography>
                )}
                
                {phase.entryCriteria && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Entry Criteria:</strong> {phase.entryCriteria}
                  </Typography>
                )}
                
                {phase.exitActions.length > 0 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Exit Actions:</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {phase.exitActions.map((action, idx) => (
                        <Chip
                          key={idx}
                          label={action}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                
                {!phase.description && !phase.entryCriteria && phase.exitActions.length === 0 && (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    No additional details configured
                  </Typography>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </List>
    </Paper>
  );
};

export default PhaseList;

